import { useCallback, useEffect, useState } from "react";
import { BellRing, RefreshCw, MailWarning, MailCheck, Clock, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ReminderPicker, { offsetLabel } from "./ReminderPicker";
import Microsoft365Card from "./Microsoft365Card";
import { fmtDateTime } from "../lib/format";

const FOLLOWUPS = [["none", "Nessun sollecito"], ["1h", "Dopo 1 ora"], ["4h", "Dopo 4 ore"], ["1d", "Dopo 1 giorno"], ["daily", "Ogni giorno fino al completamento"]];
const ST = { pending: ["In attesa", Clock, "text-slate-500"], deferred: ["Posticipato (orario silenzioso)", Clock, "text-amber-600"], processing: ["In elaborazione", RefreshCw, "text-blue-600"],
  sent: ["Inviato", CheckCircle2, "text-emerald-600"], failed: ["Fallito", XCircle, "text-red-600"], cancelled: ["Annullato", XCircle, "text-slate-400"], skipped: ["Saltato", AlertTriangle, "text-amber-600"], retry: ["Nuovo tentativo", RefreshCw, "text-amber-600"] };

function Status({ s, testid }) { const [l, I, c] = ST[s] || [s, Clock, "text-slate-500"]; return <span data-testid={testid} className={`inline-flex items-center gap-1 text-xs ${c}`}><I className="w-3.5 h-3.5" />{l}</span>; }

export default function NotificationSettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const [{ data }, { data: rem }] = await Promise.all([api.get("/notifications/settings"), api.get("/notifications/reminders", { params: { limit: 15 } })]);
    setCfg(data); setLog(rem);
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const { data } = await api.put("/notifications/settings", { enabled: cfg.enabled, default_offsets: cfg.default_offsets, overdue_followup: cfg.overdue_followup, recipient_mode: cfg.recipient_mode, fixed_email: cfg.fixed_email, quiet_hours: cfg.quiet_hours, timezone: cfg.timezone });
      setCfg(data); toast.success("Impostazioni notifiche salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const tick = async () => {
    setBusy(true);
    try { const { data } = await api.post("/notifications/run-tick"); toast.success(`Controllo eseguito: ${data.processed ?? 0} reminder elaborati`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  const ch = cfg.channel || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5" data-testid="notification-settings-panel">
      <div>
        <p className="text-sm font-heading font-semibold flex items-center gap-2 mb-1"><BellRing className="w-4 h-4 text-teal-600" /> Notifiche e Promemoria</p>
        <p className="text-sm text-slate-500">Promemoria automatici server-side per le Attività: notifica CRM + email all'owner. Ogni attività può sovrascrivere il reminder predefinito.</p>
      </div>

      <Microsoft365Card />

      <div data-testid="email-channel-status" className={`rounded-lg border px-3 py-2.5 flex items-start gap-2.5 text-sm ${ch.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
        {ch.configured ? <MailCheck className="w-4 h-4 mt-0.5" /> : <MailWarning className="w-4 h-4 mt-0.5" />}
        <div className="flex-1">
          <p className="font-medium">{ch.configured ? `Canale email reminder: ${ch.label}` : "Canale email non configurato"}</p>
          {ch.configured && !cfg.email_dispatch_enabled && <p className="text-xs mt-1">L'invio automatico delle email di promemoria tramite Microsoft 365 è <strong>non ancora attivato</strong>: i reminder generano notifica CRM e Timeline, l'email risulta "saltata" finché non viene autorizzata l'attivazione.</p>}
          {ch.configured && cfg.email_dispatch_enabled && <p className="text-xs mt-1" data-testid="email-dispatch-active">Invio automatico <strong>attivo</strong>: lo scheduler server-side (tick ogni 60s + cron ogni 15 min, timezone {cfg.timezone}) elabora i promemoria anche a CRM chiuso. Ultimo controllo: {cfg.last_run_at ? fmtDateTime(cfg.last_run_at) : "—"}.</p>}
          {!ch.configured && <p className="text-xs mt-1">Le email verranno inviate tramite Microsoft 365 (Graph API, OAuth 2.0) con l'account Ferranti &amp; Partners. Le notifiche interne CRM funzionano già. Variabili richieste nel backend: <span className="font-mono">{(cfg.required_env || []).join(", ")}</span>.</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 sm:col-span-2">
          <div><p className="text-sm font-medium text-slate-800">Attiva notifiche email attività</p><p className="text-xs text-slate-500">Se disattivato restano solo le notifiche interne CRM</p></div>
          <Switch data-testid="notif-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Reminder predefinito nuove attività</Label>
          <ReminderPicker testid="notif-default-reminders" value={cfg.default_offsets} onChange={(v) => set("default_offsets", v)} defaults={cfg.default_offsets} />
        </div>
        <div>
          <Label className="text-xs">Sollecito attività scadute</Label>
          <Select value={cfg.overdue_followup} onValueChange={(v) => set("overdue_followup", v)}>
            <SelectTrigger data-testid="notif-overdue-followup"><SelectValue /></SelectTrigger>
            <SelectContent>{FOLLOWUPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Email destinatario</Label>
          <Select value={cfg.recipient_mode} onValueChange={(v) => set("recipient_mode", v)}>
            <SelectTrigger data-testid="notif-recipient-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="owner">Owner / responsabile dell'attività</SelectItem><SelectItem value="fixed">Indirizzo fisso</SelectItem></SelectContent>
          </Select>
          {cfg.recipient_mode === "fixed" && <Input data-testid="notif-fixed-email" type="email" className="mt-2" placeholder="info@ferranti.eu" value={cfg.fixed_email || ""} onChange={(e) => set("fixed_email", e.target.value)} />}
        </div>
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <div className="flex items-center justify-between"><p className="text-sm font-medium text-slate-800">Orario silenzioso</p><Switch data-testid="notif-quiet-enabled" checked={!!cfg.quiet_hours?.enabled} onCheckedChange={(v) => set("quiet_hours", { ...cfg.quiet_hours, enabled: v })} /></div>
          <div className="flex items-center gap-2 text-sm">
            <Input data-testid="notif-quiet-start" type="time" value={cfg.quiet_hours?.start || "20:00"} onChange={(e) => set("quiet_hours", { ...cfg.quiet_hours, start: e.target.value })} className="h-9 w-28" />
            <span className="text-slate-400">→</span>
            <Input data-testid="notif-quiet-end" type="time" value={cfg.quiet_hours?.end || "08:00"} onChange={(e) => set("quiet_hours", { ...cfg.quiet_hours, end: e.target.value })} className="h-9 w-28" />
          </div>
          <p className="text-xs text-slate-500">Le email vengono posticipate alla fine della fascia; le notifiche CRM restano immediate.</p>
        </div>
        <div>
          <Label className="text-xs">Timezone predefinita</Label>
          <Input data-testid="notif-timezone" value={cfg.timezone || "Europe/Rome"} onChange={(e) => set("timezone", e.target.value)} />
          <p className="text-xs text-slate-400 mt-1">Ultimo controllo: {cfg.last_run_at ? fmtDateTime(cfg.last_run_at) : "—"}{cfg.last_stats ? ` · ${cfg.last_stats.processed} elaborati (${cfg.last_stats.source})` : ""}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="notif-save-btn" onClick={save} className="bg-navy-900 hover:bg-navy-700 text-white">Salva impostazioni</Button>
        <Button data-testid="notif-run-tick-btn" variant="outline" onClick={tick} disabled={busy} className="gap-1.5"><RefreshCw className="w-4 h-4" /> Esegui controllo ora</Button>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Ultimi promemoria</p>
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg" data-testid="reminder-log">
          {log.length === 0 && <p className="text-sm text-slate-400 px-3 py-4 text-center">Nessun promemoria programmato</p>}
          {log.map((r) => (
            <div key={r.id} data-testid={`reminder-log-${r.id}`} className="px-3 py-2 flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <p className="truncate text-slate-800">{r.activity_subject || r.activity_id}</p>
                <p className="text-xs text-slate-400">{r.kind === "overdue" ? "Sollecito scaduta" : r.kind === "due" ? "Alla scadenza" : offsetLabel(r.offset_minutes)} · {fmtDateTime(r.scheduled_at)}</p>
              </div>
              <div className="text-right space-y-0.5">
                <Status s={r.status} testid={`reminder-status-${r.id}`} />
                <p className="text-[11px] text-slate-400">CRM: {ST[r.channels?.inapp?.status]?.[0] || "—"} · Email: {ST[r.channels?.email?.status]?.[0] || "—"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
